"use client";

import React, { useEffect, useMemo } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ShopSidebar } from "@/components/ShopSidebar";
import { ProductCard } from "@/components/ProductCard";
import { ProductSkeleton } from "@/components/common/ProductSkeleton";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import {
  getALlProductForWeb,
  getCostToCostProductForWeb,
  getOtherProductForWeb,
} from "@/modules/shop/shop.service";
import {
  setCurrentPage,
  setShopFilter,
  clearShopFilters,
  setActiveShopPath,
} from "@/modules/shop/shop.slice";
import { X as CloseIcon, Menu, SlidersHorizontal } from "lucide-react";
import { Drawer } from "@/components/common/Drawer";
import { ICMS } from "../cms/cmsModel";
import { createLookupObject } from "@/helper/helper";
import { PageBanner } from "@/components/common/PageBanner";

interface ShopClientProps {
  cmsData?: ICMS[];
  isCostToCostMode?: boolean;
  isOtherProductsMode?: boolean;
}

export default function ShopClient({
  cmsData,
  isCostToCostMode = false,
  isOtherProductsMode = false,
}: ShopClientProps) {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const urlCategory = searchParams.get("category");

  const [CMSData, setCMSData] = React.useState<any>({});
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const { ShopProductList, totalCount, filters, currentPage, loading, activeShopPath } =
    useAppSelector((state) => state.shop);

  const { isCustomerAuthenticated, user } = useAppSelector(
    (state) => state.auth,
  );
  const showLoginDiscountMsg =
    !isCustomerAuthenticated || (isCustomerAuthenticated && user?.isFirstOrder);

  const [searchTerm, setSearchTerm] = React.useState(filters.ProductName);
  const lastFetchedFilters = React.useRef<string | null>(null);

  // Clear products and filters when navigating between different shop modes
  useEffect(() => {
    if (activeShopPath !== pathname) {
      dispatch(clearShopFilters());
      dispatch(setActiveShopPath(pathname));
    }
  }, [pathname, dispatch, activeShopPath]);

  // Synchronized Filter & URL Params Fetching
  useEffect(() => {
    // Prevent fetching with stale state during path transitions
    if (activeShopPath !== pathname) {
      return;
    }

    if (urlCategory && filters.CategoryIDs !== urlCategory) {
      dispatch(
        setShopFilter({
          CategoryIDs: urlCategory,
          ProductName: "",
          PriceFilter: "",
          Skip: 0,
        }),
      );
      router.replace(pathname);
      return;
    }

    const currentFetchKey = JSON.stringify({ filters, pathname });

    // Skip API call if the exact same filters/path were just fetched
    if (lastFetchedFilters.current === currentFetchKey) {
      return;
    }

    // Skip API call on initial remount if we already have the product list (e.g. returning from details)
    if (!lastFetchedFilters.current && ShopProductList.length > 0) {
      lastFetchedFilters.current = currentFetchKey;
      return;
    }

    lastFetchedFilters.current = currentFetchKey;

    if (isCostToCostMode) {
      dispatch(getCostToCostProductForWeb(filters));
    } else if (isOtherProductsMode) {
      dispatch(getOtherProductForWeb(filters));
    } else {
      dispatch(getALlProductForWeb(filters));
    }
  }, [
    dispatch,
    filters,
    urlCategory,
    router,
    pathname,
    activeShopPath,
    isCostToCostMode,
    isOtherProductsMode,
  ]);

  // Sync local searchTerm with Redux filters
  useEffect(() => {
    setSearchTerm(filters.ProductName);
  }, [filters.ProductName]);

  const [sortBy, setSortBy] = React.useState("Featured");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== filters.ProductName) {
        dispatch(setShopFilter({ ProductName: searchTerm }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, dispatch, filters.ProductName]);

  const sortedProducts = useMemo(() => {
    const products = [...ShopProductList];
    if (sortBy === "Price: Low to High") {
      return products.sort((a, b) => a.Price - b.Price);
    } else if (sortBy === "Price: High to Low") {
      return products.sort((a, b) => b.Price - a.Price);
    }
    return products;
  }, [ShopProductList, sortBy]);

  const handleLoadMore = () => {
    dispatch(setCurrentPage(currentPage + 1));
  };

  return (
    <main className="min-h-screen">
      <Navbar />

      {CMSData?.["Shop - Section 1 : Banner"] ? (
        <div
          className="banner-section border-b border-border-1 p-10 md:p-14 relative overflow-hidden"
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Shop - Section 1 : Banner"]?.Template,
          }}
        />
      ) : (
        <div className="banner-section border-b border-border-1 p-10 md:p-14 relative overflow-hidden">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[120px] bg-gold-pool opacity-5 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-3.5 text-[10px] font-semibold tracking-[0.28em] uppercase text-black-3 mb-4 before:h-px before:w-15 before:bg-gradient-to-r before:from-white before:to-transparent">
              {isCostToCostMode
                ? "Cost To Cost"
                : isOtherProductsMode
                  ? "Other Products"
                  : "Shop"}
            </div>
            <h1 className="font-cormorant text-[clamp(36px,5vw,64px)] font-light leading-[1.1] mb-3">
              {isCostToCostMode
                ? "Factory "
                : isOtherProductsMode
                  ? "Exclusive "
                  : "Our "}{" "}
              <em className="italic accent-text-gradient">
                {isCostToCostMode
                  ? "Pricing"
                  : isOtherProductsMode
                    ? "Deals"
                    : "Collection"}
              </em>
            </h1>
            <p className="text-sm font-light leading-relaxed max-w-lg">
              {isCostToCostMode
                ? "Exclusive direct-from-factory rates. These items are sold at cost and are ineligible for further discounts."
                : isOtherProductsMode
                  ? "Handpicked exclusive products from our special collection."
                  : "Factory-direct wholesale pricing. "}
              {!isCostToCostMode &&
                (showLoginDiscountMsg
                  ? "Login to get 5% discount on orders over ₹3,000."
                  : "Exclusive member wholesale rates applied.")}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row min-h-[70vh] bg-black">
        {/* Desktop Sidebar (hidden on mobile/tablet) */}
        <div className="hidden lg:block">
          <ShopSidebar
            isCostToCost={isCostToCostMode}
            isOthersProduct={isOtherProductsMode}
          />
        </div>

        {/* Mobile Left Drawer for Filters */}
        <Drawer
          isOpen={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
          position="left"
          variant="dark"
          title="Filters"
          isOverlayClick={true}
          isEscKey={true}
        >
          <ShopSidebar
            isDrawer={true}
            onClose={() => setIsMobileFilterOpen(false)}
            isCostToCost={isCostToCostMode}
            isOthersProduct={isOtherProductsMode}
          />
        </Drawer>

        <main className="flex-1 p-5 md:p-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-7 pb-4 border-b border-border-1">
            <div className="flex items-center gap-3 text-[13px] text-text-2">
              {/* Mobile Hamburger Filter Button */}
              <button
                onClick={() => setIsMobileFilterOpen(true)}
                className="lg:hidden flex items-center gap-2 px-3.5 py-2 bg-black-2 border border-gold-dim/60 text-gold-core hover:border-gold-core text-xs font-semibold uppercase tracking-wider rounded-sm transition-all cursor-pointer shadow-sm active:scale-95"
                aria-label="Open Filter Drawer"
              >
                <Menu size={16} className="text-gold-core" />
                <SlidersHorizontal size={14} className="text-gold-core" />
                <span>Filters</span>
                {(filters.CategoryIDs ||
                  filters.PriceFilter ||
                  filters.ProductName) && (
                  <span className="w-2 h-2 rounded-full bg-gold-core animate-pulse" />
                )}
              </button>

              <div>
                Showing{" "}
                <strong className="text-text-1">
                  {ShopProductList.length} products
                </strong>{" "}
                of {totalCount} —{" "}
                <span className="hidden sm:inline">
                  {showLoginDiscountMsg
                    ? "5% discount on ₹3000+"
                    : "member wholesale prices"}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  className="bg-black-2 border border-border-1 text-text-1 px-3.5 py-2.5 pr-8 text-xs outline-none focus:border-gold-dim w-48 shadow-sm rounded-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-3 hover:text-text-1 transition-colors"
                  >
                    <CloseIcon size={14} />
                  </button>
                )}
              </div>
              <select
                className="bg-black-2 border border-border-1 text-text-1 px-3.5 py-2 text-xs outline-none cursor-pointer focus:border-gold-dim shadow-sm rounded-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option>Sort: Featured</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
                <option>Newest First</option>
              </select>
            </div>
          </div>

          <div
            className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 border-t border-l border-border-1 transition-opacity shadow-sm`}
          >
            {sortedProducts.length > 0 ? (
              sortedProducts.map((product) => (
                <ProductCard
                  key={product.ID}
                  id={product.ID}
                  url={product.URL}
                  name={product.ProductName}
                  category={product.CategoryName}
                  price={product.Price}
                  originalPrice={product.Price}
                  image={product.MainImage}
                  badge={product.Qty < 5 ? "Low Stock" : ""}
                  gstPer={
                    product.GSTPer !== undefined
                      ? product.GSTPer
                      : (product as any).gstPer ||
                        (product as any).GstPer ||
                        (product as any).GSTper
                  }
                  minimumQty={product.MinimumQty}
                  isCostToCost={
                    product.IsCostToCost || (product as any).isCostToCost
                  }
                  isOthersProduct={
                    product.IsOthersProduct || (product as any).isOthersProduct
                  }
                />
              ))
            ) : loading ? (
              Array.from({ length: filters.Limit || 10 }).map((_, i) => (
                <ProductSkeleton key={`skeleton-${i}`} />
              ))
            ) : (
              <div className="col-span-full py-32 text-center bg-black-2 border-b border-r border-border-1">
                <p className="text-text-3 font-light italic text-sm">
                  No products match your selected filters.
                </p>
              </div>
            )}

            {loading &&
              Array.from({ length: filters.Limit || 10 }).map((_, i) => (
                <ProductSkeleton key={`skeleton-${i}`} />
              ))}
          </div>

          {ShopProductList.length < totalCount && (
            <div className="mt-12 flex justify-center items-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-8 py-3 bg-black-2 border border-gold-dim text-gold-core hover:bg-gold-core hover:text-white transition-all rounded-sm tracking-widest text-xs font-semibold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </main>
      </div>

      <Footer />
    </main>
  );
}
